import { Link } from "react-router-dom";
import "./historique.css";
import { FormEvent, useEffect, useMemo, useState } from "react";
import HistoriqueCommande from "../../../Services/Admin/HistoriqueCommande";
import { Commande } from "../../../Modeles/Commande";
import axios from "axios";
import { NewSideBarAdmin } from "../../NewSideBarAdmin";
import { formatCodeCommande } from "../../../Services/CodeTrackingHelper";

export interface commandeType {
  product: Commande[];
  messageErros: string;
}

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 992);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return isMobile;
};

export default function Historique() {
  const isMobile = useIsMobile();
  const [state, setState] = useState<commandeType>({
    product: [] as Commande[],
    messageErros: "Aucune commande",
  });
  const [Message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);

  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");

  const handleSubmit = async (e: FormEvent, idCommande: any) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("idCommande", idCommande);
    try {
      await axios.post(
        `${process.env.REACT_APP_PHP_APP_URL}/commande/confirme`,
        formData
      );
      setMessage("Vous avez confirmé avec succès la commande");
      setTimeout(() => setMessage(""), 2500);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleChangePage = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setPaymentFilter("all");
    setCurrentPage(1);
  };

  useEffect(() => {
    setLoading(true);
    HistoriqueCommande()
      .getCommandeAdmin()
      .then((res) => {
        setState((prev) => ({ ...prev, product: Array.isArray(res.data) ? res.data : [] }));
        setLoading(false);
      })
      .catch((err: any) => {
        setState((prev) => ({
          ...prev,
          messageErros: err.messageErros || "Erreur de récupération des commandes",
        }));
        setLoading(false);
      });
  }, []);

  const { product, messageErros } = state;

  // Helper to extract total order amount robustly
  const getOrderAmount = (item: any): number => {
    const raw = item.TotalCommandeHT || item.TotalCommandeTTC || item.Total || 0;
    const num = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(',', '.'));
    return isNaN(num) ? 0 : num;
  };

  // Compute KPI stats
  const stats = useMemo(() => {
    const totalOrders = product.length;
    let totalRevenue = 0;
    let confirmedCount = 0;
    let pendingCount = 0;
    let validatedCount = 0;
    let cancelledCount = 0;

    product.forEach((item) => {
      const amount = getOrderAmount(item);
      totalRevenue += amount;

      const st = item.Statut?.toLowerCase().trim();
      if (st === "confirmé" || st === "confirme") {
        confirmedCount++;
      } else if (st === "annulé" || st === "annule") {
        cancelledCount++;
      } else if (st === "valider" || st === "validé" || st === "valide") {
        validatedCount++;
      } else {
        pendingCount++;
      }
    });

    return {
      totalOrders,
      totalRevenue,
      confirmedCount,
      pendingCount,
      validatedCount,
      cancelledCount,
    };
  }, [product]);

  // Filtered list
  const filteredProducts = useMemo(() => {
    return product.filter((item) => {
      const searchLower = searchTerm.toLowerCase().trim();
      const trackingCode = formatCodeCommande(item.RefCommande, item.IdCommande || item.id, item.DateCommande);
      const codeMatch = trackingCode.toLowerCase().includes(searchLower);
      const idMatch = String(item.IdCommande || item.id || "").toLowerCase().includes(searchLower);
      const magasinMatch = (item.NomMagasin || "").toLowerCase().includes(searchLower);
      const clientMatch = (item.NomClient || "").toLowerCase().includes(searchLower);
      const livraisonMatch = (item.ModeLivraison || "").toLowerCase().includes(searchLower);
      const methodMatch = (item.MethodPayment || "").toLowerCase().includes(searchLower);
      const adresseMatch = (item.Adresse || "").toLowerCase().includes(searchLower);
      const adresseRetraitMatch = (item.AdresseRetreit || "").toLowerCase().includes(searchLower);
      const magasinRetraitMatch = (item.MagasinRetrait || "").toLowerCase().includes(searchLower);

      const matchesSearch =
        searchTerm.trim() === "" ||
        codeMatch ||
        idMatch ||
        magasinMatch ||
        clientMatch ||
        livraisonMatch ||
        methodMatch ||
        adresseMatch ||
        adresseRetraitMatch ||
        magasinRetraitMatch;

      const statusLower = (item.Statut || "").toLowerCase().trim();
      let matchesStatus = true;
      if (statusFilter !== "all") {
        if (statusFilter === "confirmé") {
          matchesStatus = statusLower === "confirmé" || statusLower === "confirme";
        } else if (statusFilter === "annulé") {
          matchesStatus = statusLower === "annulé" || statusLower === "annule";
        } else if (statusFilter === "valider") {
          matchesStatus = statusLower === "valider" || statusLower === "validé" || statusLower === "valide";
        } else if (statusFilter === "en cours") {
          matchesStatus = statusLower.includes("cours") || (!["confirmé", "confirme", "annulé", "annule", "valider", "validé"].includes(statusLower));
        }
      }

      let matchesPayment = true;
      if (paymentFilter !== "all") {
        const payStatusLower = (item.StatutPayment || "").toLowerCase().trim();
        matchesPayment = payStatusLower.includes(paymentFilter.toLowerCase());
      }

      return matchesSearch && matchesStatus && matchesPayment;
    });
  }, [product, searchTerm, statusFilter, paymentFilter]);

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);

  const getStatusConfig = (status: string) => {
    const statusLower = status?.toLowerCase()?.trim() || "";
    if (statusLower === "confirmé" || statusLower === "confirme") {
      return {
        label: "Confirmé",
        color: "#059669",
        bg: "#ecfdf5",
        border: "#a7f3d0",
        icon: "check-circle-fill",
        pillClass: "badge-success",
      };
    }
    if (statusLower === "annulé" || statusLower === "annule") {
      return {
        label: "Annulé",
        color: "#dc2626",
        bg: "#fef2f2",
        border: "#fecaca",
        icon: "x-circle-fill",
        pillClass: "badge-danger",
      };
    }
    if (statusLower === "valider" || statusLower === "validé" || statusLower === "valide") {
      return {
        label: "Validé",
        color: "#2563eb",
        bg: "#eff6ff",
        border: "#bfdbfe",
        icon: "patch-check-fill",
        pillClass: "badge-info",
      };
    }
    return {
      label: status || "En cours",
      color: "#d97706",
      bg: "#fffbeb",
      border: "#fde68a",
      icon: "clock-fill",
      pillClass: "badge-warning",
    };
  };

  const getPaymentStatusConfig = (payStatus: string) => {
    const lower = (payStatus || "").toLowerCase().trim();
    if (lower.includes("payé") || lower.includes("paye") || lower === "paid" || lower === "oui") {
      return { label: payStatus || "Payé", class: "pay-badge-paid", icon: "check2-circle" };
    }
    if (lower.includes("non") || lower.includes("unpaid") || lower.includes("refus")) {
      return { label: payStatus || "Non payé", class: "pay-badge-unpaid", icon: "exclamation-circle" };
    }
    return { label: payStatus || "En attente", class: "pay-badge-pending", icon: "hourglass-split" };
  };

  const hasActiveFilters = searchTerm !== "" || statusFilter !== "all" || paymentFilter !== "all";

  return (
    <>
      <NewSideBarAdmin />

      {loading ? (
        <div className="loader-container">
          <div className="loader-content">
            <div className="spinner-modern"></div>
            <p className="loader-text">Chargement de l'historique...</p>
          </div>
        </div>
      ) : (
        <div className={isMobile ? "containerSuivi" : "main-container"}>

          {/* ── TOP HEADER ── */}
          <div className="page-header">
            <div className="header-content">
              <div className="header-badge">
                <i className="bi bi-shield-check"></i>
                <span>Administration</span>
              </div>
              <h1 className="page-title">
                <i className="bi bi-clock-history"></i>
                Historique des Commandes
              </h1>
              <p className="page-subtitle">
                Visualisez, filtrez et gérez l'ensemble des commandes magasins en temps réel
              </p>
            </div>

            <div className="header-actions">
              <Link className="devis-link" to={"/devis"}>
                <button className="devis-button">
                  <i className="bi bi-file-earmark-text"></i>
                  <span>Consulter les devis</span>
                  <i className="bi bi-arrow-right"></i>
                </button>
              </Link>
            </div>
          </div>

          {/* ── KPI STAT CARDS ── */}
          <div className="stats-grid">
            <div className="stat-card stat-total">
              <div className="stat-icon-wrapper">
                <i className="bi bi-bag-check"></i>
              </div>
              <div className="stat-details">
                <span className="stat-label">Total Commandes</span>
                <span className="stat-value">{stats.totalOrders}</span>
                <span className="stat-meta">
                  CA: <strong>{stats.totalRevenue.toLocaleString("fr-FR")} MAD</strong>
                </span>
              </div>
            </div>

            <div className="stat-card stat-confirmed">
              <div className="stat-icon-wrapper">
                <i className="bi bi-check-circle"></i>
              </div>
              <div className="stat-details">
                <span className="stat-label">Confirmées</span>
                <span className="stat-value">{stats.confirmedCount}</span>
                <span className="stat-meta">
                  {stats.totalOrders > 0
                    ? `${Math.round((stats.confirmedCount / stats.totalOrders) * 100)}% du total`
                    : "0%"}
                </span>
              </div>
            </div>

            <div className="stat-card stat-pending">
              <div className="stat-icon-wrapper">
                <i className="bi bi-clock"></i>
              </div>
              <div className="stat-details">
                <span className="stat-label">En cours / Validées</span>
                <span className="stat-value">
                  {stats.pendingCount + stats.validatedCount}
                </span>
                <span className="stat-meta">À traiter</span>
              </div>
            </div>

            <div className="stat-card stat-cancelled">
              <div className="stat-icon-wrapper">
                <i className="bi bi-x-circle"></i>
              </div>
              <div className="stat-details">
                <span className="stat-label">Annulées</span>
                <span className="stat-value">{stats.cancelledCount}</span>
                <span className="stat-meta">
                  {stats.totalOrders > 0
                    ? `${Math.round((stats.cancelledCount / stats.totalOrders) * 100)}% du total`
                    : "0%"}
                </span>
              </div>
            </div>
          </div>

          {/* ── SUCCESS ALERT ── */}
          {Message !== "" && (
            <div className="success-alert">
              <i className="bi bi-check-circle-fill"></i>
              <span>{Message}</span>
            </div>
          )}

          {/* ── TOOLBAR: SEARCH & STATUS FILTERS ── */}
          <div className="toolbar-card">
            <div className="toolbar-row">
              {/* Search input */}
              <div className="search-box">
                <i className="bi bi-search search-icon"></i>
                <input
                  type="text"
                  placeholder="Rechercher par ID (#12), magasin, client, adresse, livraison..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="search-input"
                />
                {searchTerm && (
                  <button
                    className="search-clear-btn"
                    onClick={() => setSearchTerm("")}
                    title="Effacer la recherche"
                  >
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </div>

              {/* Status pills selector */}
              <div className="status-pills">
                <button
                  className={`status-pill ${statusFilter === "all" ? "active" : ""}`}
                  onClick={() => handleStatusFilterChange("all")}
                >
                  Tous ({product.length})
                </button>
                <button
                  className={`status-pill status-pill-confirmed ${statusFilter === "confirmé" ? "active" : ""}`}
                  onClick={() => handleStatusFilterChange("confirmé")}
                >
                  <i className="bi bi-check-circle-fill"></i>
                  Confirmé ({stats.confirmedCount})
                </button>
                <button
                  className={`status-pill status-pill-pending ${statusFilter === "en cours" ? "active" : ""}`}
                  onClick={() => handleStatusFilterChange("en cours")}
                >
                  <i className="bi bi-clock-fill"></i>
                  En cours ({stats.pendingCount})
                </button>
                <button
                  className={`status-pill status-pill-validated ${statusFilter === "valider" ? "active" : ""}`}
                  onClick={() => handleStatusFilterChange("valider")}
                >
                  <i className="bi bi-patch-check-fill"></i>
                  Validé ({stats.validatedCount})
                </button>
                <button
                  className={`status-pill status-pill-cancelled ${statusFilter === "annulé" ? "active" : ""}`}
                  onClick={() => handleStatusFilterChange("annulé")}
                >
                  <i className="bi bi-x-circle-fill"></i>
                  Annulé ({stats.cancelledCount})
                </button>
              </div>

              {/* Reset filter button if any active */}
              {hasActiveFilters && (
                <button
                  className="btn-reset-filters"
                  onClick={handleResetFilters}
                  title="Réinitialiser tous les filtres"
                >
                  <i className="bi bi-arrow-counterclockwise"></i>
                  <span>Réinitialiser</span>
                </button>
              )}
            </div>

            {/* Results counter subtitle */}
            <div className="toolbar-info">
              <span>
                <i className="bi bi-funnel"></i>
                Résultats: <strong>{filteredProducts.length}</strong> commande{filteredProducts.length > 1 ? "s" : ""} trouvée{filteredProducts.length > 1 ? "s" : ""}
              </span>
              {hasActiveFilters && (
                <span className="filter-active-indicator">
                  (Filtres actifs)
                </span>
              )}
            </div>
          </div>

          {/* ── MOBILE: CARDS VIEW ── */}
          {isMobile ? (
            <div className="cards-grid">
              {currentItems.length > 0 ? (
                currentItems.map((item, index) => {
                  const statusConfig = getStatusConfig(item.Statut);
                  const payConfig = getPaymentStatusConfig(item.StatutPayment);
                  const orderId = item.IdCommande || item.id || (indexOfFirstItem + index + 1);
                  const trackingCode = formatCodeCommande(item.RefCommande, item.IdCommande || item.id, item.DateCommande);

                  return (
                    <div key={index} className="order-card">
                      {/* Card Header */}
                      <div className="card-header">
                        <div className="order-id-badge" title="Code de suivi de la commande">
                          <span className="order-num">{trackingCode}</span>
                        </div>
                        <div
                          className="status-badge"
                          style={{
                            color: statusConfig.color,
                            backgroundColor: statusConfig.bg,
                            borderColor: statusConfig.border,
                          }}
                        >
                          <i className={`bi bi-${statusConfig.icon}`}></i>
                          {statusConfig.label}
                        </div>
                      </div>

                      {/* Card Body */}
                      <div className="card-body">
                        {/* Client & Magasin */}
                        <div className="card-entity-section">
                          <div className="entity-item">
                            <i className="bi bi-person-circle entity-icon client"></i>
                            <div>
                              <div className="entity-sublabel">Client</div>
                              <div className="entity-main-value">{item.NomClient || "Client standard"}</div>
                            </div>
                          </div>
                          <div className="entity-item">
                            <i className="bi bi-shop entity-icon shop"></i>
                            <div>
                              <div className="entity-sublabel">Magasin</div>
                              <div className="entity-main-value">{item.NomMagasin || "Magasin"}</div>
                            </div>
                          </div>
                        </div>

                        <div className="card-divider"></div>

                        {/* Amount & Date */}
                        <div className="card-info-grid">
                          <div className="info-cell">
                            <span className="info-cell-label">
                              <i className="bi bi-wallet2"></i> Montant Total
                            </span>
                            <span className="info-cell-value amount-primary">
                              {getOrderAmount(item).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD
                            </span>
                            {item.FraisLivraison ? (
                              <span className="info-cell-sub">
                                + {item.FraisLivraison} MAD frais
                              </span>
                            ) : null}
                          </div>

                          <div className="info-cell">
                            <span className="info-cell-label">
                              <i className="bi bi-calendar3"></i> Date Commande
                            </span>
                            <span className="info-cell-value date-text">
                              {item.DateCommande || "N/A"}
                            </span>
                          </div>
                        </div>

                        <div className="card-divider"></div>

                        {/* Payment & Delivery */}
                        <div className="card-info-grid">
                          <div className="info-cell">
                            <span className="info-cell-label">
                              <i className="bi bi-credit-card"></i> Paiement
                            </span>
                            <div className="badge-group">
                              <span className={`pay-pill ${payConfig.class}`}>
                                <i className={`bi bi-${payConfig.icon}`}></i>
                                {payConfig.label}
                              </span>
                              {item.MethodPayment && (
                                <span className="pay-method-tag">
                                  {item.MethodPayment}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="info-cell">
                            <span className="info-cell-label">
                              <i className="bi bi-truck"></i> Mode Livraison
                            </span>
                            <span className="delivery-mode-tag">
                              <i className={item.ModeLivraison?.toLowerCase().includes("retrait") ? "bi bi-shop" : "bi bi-box-seam"}></i>
                              {item.ModeLivraison || "Standard"}
                            </span>
                          </div>
                        </div>

                        <div className="card-divider"></div>

                        {/* ── ADDRESSES SECTION ── */}
                        <div className="card-info-grid">
                          <div className="info-cell">
                            <span className="info-cell-label">
                              <i className="bi bi-geo-alt"></i> Adresse
                            </span>
                            <span className="info-cell-value text-address">
                              {item.Adresse || "—"}
                            </span>
                          </div>

                          <div className="info-cell">
                            <span className="info-cell-label">
                              <i className="bi bi-shop-window"></i> Adresse Retrait
                            </span>
                            <span className="info-cell-value text-address">
                              {[item.MagasinRetrait, item.AdresseRetreit].filter(Boolean).join(" - ") || "—"}
                            </span>
                          </div>
                        </div>

                      </div>

                      {/* Card Footer */}
                      <div className="card-footer">
                        <Link
                          className="detail-button"
                          to={`/historiques/${orderId}`}
                        >
                          <span>Voir les détails de la commande</span>
                          <i className="bi bi-arrow-right-circle"></i>
                        </Link>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="empty-state">
                  <i className="bi bi-inbox"></i>
                  <p>{hasActiveFilters ? "Aucune commande ne correspond aux filtres sélectionnés." : messageErros}</p>
                  {hasActiveFilters && (
                    <button className="btn-reset-empty" onClick={handleResetFilters}>
                      <i className="bi bi-arrow-counterclockwise"></i>
                      Réinitialiser les filtres
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* ── DESKTOP: TABLE VIEW ── */
            <div className="table-card">
              <div className="table-scroll-wrapper">
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th className="th-id">
                        <i className="bi bi-upc-scan"></i> N° Suivi Commande
                      </th>
                      <th className="th-client-magasin">
                        <i className="bi bi-people"></i> Client & Magasin
                      </th>
                      <th className="th-montant">
                        <i className="bi bi-wallet2"></i> Montant Total
                      </th>
                      <th className="th-statut">
                        <i className="bi bi-flag"></i> Statut Commande
                      </th>
                      <th className="th-paiement">
                        <i className="bi bi-credit-card"></i> Paiement
                      </th>
                      <th className="th-livraison">
                        <i className="bi bi-truck"></i> Mode Livraison
                      </th>
                      <th className="th-adresse">
                        <i className="bi bi-geo-alt"></i> Adresse
                      </th>
                      <th className="th-adresse-retrait">
                        <i className="bi bi-shop-window"></i> Adresse Retrait
                      </th>
                      <th className="th-date">
                        <i className="bi bi-calendar-event"></i> Date
                      </th>
                      <th className="th-actions text-center">
                        <i className="bi bi-gear"></i> Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.length > 0 ? (
                      currentItems.map((item, index) => {
                        const statusConfig = getStatusConfig(item.Statut);
                        const payConfig = getPaymentStatusConfig(item.StatutPayment);
                        const orderId = item.IdCommande || item.id || (indexOfFirstItem + index + 1);
                        const trackingCode = formatCodeCommande(item.RefCommande, item.IdCommande || item.id, item.DateCommande);

                        return (
                          <tr key={index} className="table-row-hover">
                            {/* ID / Tracking Code */}
                            <td className="cell-id">
                              <span className="id-chip tracking-code-chip" title="Code de suivi de la commande">
                                {trackingCode}
                              </span>
                            </td>

                            {/* Client & Store */}
                            <td className="cell-client-magasin">
                              <div className="client-name">
                                <i className="bi bi-person-circle"></i>
                                <span>{item.NomClient || "Client"}</span>
                              </div>
                              <div className="store-name">
                                <i className="bi bi-shop"></i>
                                <span>{item.NomMagasin || "Magasin"}</span>
                              </div>
                            </td>

                            {/* Amount HT + Delivery Fee */}
                            <td className="cell-montant">
                              <div className="amount-ht">
                                {getOrderAmount(item).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD
                              </div>
                              {item.FraisLivraison ? (
                                <div className="amount-fee">
                                  + {item.FraisLivraison} MAD frais
                                </div>
                              ) : (
                                <div className="amount-free">Frais inclus</div>
                              )}
                            </td>

                            {/* Status */}
                            <td className="cell-statut">
                              <span
                                className={`status-badge-table ${statusConfig.pillClass}`}
                                style={{
                                  color: statusConfig.color,
                                  backgroundColor: statusConfig.bg,
                                  borderColor: statusConfig.border,
                                }}
                              >
                                <i className={`bi bi-${statusConfig.icon}`}></i>
                                {statusConfig.label}
                              </span>
                            </td>

                            {/* Payment */}
                            <td className="cell-paiement">
                              <div className="pay-status-row">
                                <span className={`pay-pill ${payConfig.class}`}>
                                  <i className={`bi bi-${payConfig.icon}`}></i>
                                  {payConfig.label}
                                </span>
                              </div>
                              {item.MethodPayment && (
                                <div className="pay-method-sub">
                                  <i className="bi bi-credit-card-2-front"></i>
                                  {item.MethodPayment}
                                </div>
                              )}
                            </td>

                            {/* Delivery Mode */}
                            <td className="cell-livraison">
                              <div className="livraison-mode">
                                <i className={item.ModeLivraison?.toLowerCase().includes("retrait") ? "bi bi-shop" : "bi bi-box-seam"}></i>
                                <span>{item.ModeLivraison || "Standard"}</span>
                              </div>
                            </td>

                            {/* ── Adresse Commande ── */}
                            <td className="cell-adresse">
                              {item.Adresse ? (
                                <div className="address-display" title={item.Adresse}>
                                  <i className="bi bi-geo-alt-fill"></i>
                                  <span>{item.Adresse}</span>
                                </div>
                              ) : (
                                <span className="text-muted-dash">—</span>
                              )}
                            </td>

                            {/* ── Adresse Retrait ── */}
                            <td className="cell-adresse-retrait">
                              {(item.AdresseRetreit || item.MagasinRetrait) ? (
                                <div
                                  className="address-display retrait-address"
                                  title={[item.MagasinRetrait, item.AdresseRetreit].filter(Boolean).join(" - ")}
                                >
                                  <i className="bi bi-shop-window"></i>
                                  <div className="retrait-lines">
                                    {item.MagasinRetrait && (
                                      <span className="retrait-store-title">{item.MagasinRetrait}</span>
                                    )}
                                    {item.AdresseRetreit && (
                                      <span className="retrait-address-sub">{item.AdresseRetreit}</span>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-muted-dash">—</span>
                              )}
                            </td>

                            {/* Date */}
                            <td className="cell-date">
                              <div className="date-display">
                                <i className="bi bi-calendar3"></i>
                                <span>{item.DateCommande || "N/A"}</span>
                              </div>
                            </td>

                            {/* Action Details */}
                            <td className="cell-actions text-center">
                              <Link
                                className="action-button-primary"
                                to={`/historiques/${orderId}`}
                                title={`Voir les détails de la commande #${orderId}`}
                              >
                                <span>Détails</span>
                                <i className="bi bi-arrow-right-circle"></i>
                              </Link>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={10} className="empty-row">
                          <div className="empty-table-state">
                            <i className="bi bi-inbox"></i>
                            <p>{hasActiveFilters ? "Aucune commande trouvée pour ces critères de recherche." : messageErros}</p>
                            {hasActiveFilters && (
                              <button className="btn-reset-empty" onClick={handleResetFilters}>
                                <i className="bi bi-arrow-counterclockwise"></i>
                                Réinitialiser les filtres
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Table footer info */}
              <div className="table-footer">
                <div className="footer-counts">
                  Affichage{" "}
                  <strong>
                    {filteredProducts.length > 0 ? indexOfFirstItem + 1 : 0}–
                    {Math.min(indexOfLastItem, filteredProducts.length)}
                  </strong>{" "}
                  sur <strong>{filteredProducts.length}</strong> commande{filteredProducts.length > 1 ? "s" : ""}
                </div>

                <div className="footer-note">
                  <i className="bi bi-info-circle"></i>
                  <span>Cliquez sur Détails pour consulter les articles et le suivi complet</span>
                </div>
              </div>
            </div>
          )}

          {/* ── PAGINATION CONTROLS ── */}
          {totalPages > 1 && (
            <div className="pagination-container">
              <button
                onClick={() => handleChangePage(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-nav-btn"
                title="Page précédente"
              >
                <i className="bi bi-chevron-left"></i>
                <span>Précédent</span>
              </button>

              <div className="page-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => {
                  // Show current page, edges, and nearby pages
                  if (
                    number === 1 ||
                    number === totalPages ||
                    (number >= currentPage - 1 && number <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={number}
                        onClick={() => handleChangePage(number)}
                        className={`page-number-btn ${currentPage === number ? "active" : ""}`}
                      >
                        {number}
                      </button>
                    );
                  } else if (
                    number === currentPage - 2 ||
                    number === currentPage + 2
                  ) {
                    return (
                      <span key={number} className="page-ellipsis">
                        ...
                      </span>
                    );
                  }
                  return null;
                })}
              </div>

              <button
                onClick={() => handleChangePage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-nav-btn"
                title="Page suivante"
              >
                <span>Suivant</span>
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          )}

        </div>
      )}
    </>
  );
}